import { Button } from '@hologram-dimension/button';
import { DropdownPanel } from '@hologram-dimension/dropdown-panel';
import clsx from 'clsx/lite';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import type {
  HologramSimIdsMap,
  OnSimsModified,
  OpenModalCallback,
  SimInventorySegmentName,
} from 'types/common';
import styles from './ManageSimButton.module.css';
import useManageSimItems from './ManageSimItems/useManageSimItems';

interface ManageSimButtonProps {
  buttonText?: string;
  subtitle?: string;
  isLoading?: boolean;
  disabled?: boolean;
  selectedSims?: HologramSimIdsMap;
  context: 'details' | 'inventory';
  segmentName?: SimInventorySegmentName;
  handleModalOpen: OpenModalCallback;
  onSimsModified: OnSimsModified;
  variant?: GetReactParameterBag<typeof Button>['variant'];
  placement?: GetReactParameterBag<typeof DropdownPanel>['placement'];
  className?: string;
}

const ManageSimButton: FC<ManageSimButtonProps> = ({
  buttonText = 'Manage SIMs',
  subtitle,
  isLoading = false,
  disabled = false,
  selectedSims,
  context,
  segmentName,
  handleModalOpen,
  onSimsModified,
  variant = 'secondary',
  placement,
  className,
}) => {
  const [internalShow, setShow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const show = internalShow && !isLoading;

  const onBodyClick: (this: HTMLElement, ev: HTMLElementEventMap['click']) => any = useCallback(
    (e) => {
      if (show) {
        let found = false;
        let parent: HTMLElement | null = e.target as any;
        while (parent !== null && !found) {
          parent = parent.parentElement;
          found = parent === containerRef.current;
        }

        if (!found) {
          setShow(false);
        }
      }
    },
    [show]
  );

  useEffect(() => {
    document.body.addEventListener('click', onBodyClick);

    return () => {
      document.body.removeEventListener('click', onBodyClick);
    };
  }, [onBodyClick]);

  const content = useManageSimItems({
    context,
    segmentName,
    selectedSims,
    handleModalOpen,
    subtitle,
    disabled,
    onSimsModified,
    open: show,
    onActionTaken: () => setShow(false),
  });

  const onClick = useCallback(() => {
    setShow((prev) => !prev);
  }, []);

  return (
    <div className={clsx(styles.wrapper, className)} ref={containerRef}>
      <DropdownPanel
        className={styles.dropdownPanel}
        content={content}
        open={show}
        onOpenChange={setShow}
        placement={placement}
      >
        <Button
          iconEnd={show ? 'ChevronSingleNorth' : 'ChevronSingleSouth'}
          variant={variant}
          onClick={onClick}
          disabled={isLoading}
          id={`sim${context}-actions-managesims`}
        >
          {buttonText}
        </Button>
      </DropdownPanel>
    </div>
  );
};

export default ManageSimButton;
